import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import TelInput from 'components/Common/TelInput';
import { useCreateA2pBrand, useUpdateA2pBrand } from '@/apis/business/setting/phoneNumbers/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { A2pBrand } from '@/apis/business/setting/phoneNumbers/types';
import {
  A2pBrandFormValues,
  EMPTY_BRAND_FORM,
  ENTITY_TYPE_OPTIONS,
  VERTICAL_OPTIONS,
  brandFormToPayload,
  brandToFormValues,
  stateOptionsForCountry
} from './a2pConstants';
import { brandDefaultsFromBusiness } from './businessFormPrefill';

type Props = {
  open: boolean;
  brand?: A2pBrand | null;
  onClose: () => void;
  onSubmitted: (brand: A2pBrand) => void;
};

const BORDER_COLOR = '#E5E7EB';

const apiErrorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

const selectMenuProps = {
  disablePortal: true,
  PaperProps: { sx: { maxHeight: 280 } }
};

const shrinkLabel = { shrink: true };

const pillSx = { textTransform: 'none', fontWeight: 500, borderRadius: '999px', px: 2.5 } as const;

const outlinedPillSx = {
  ...pillSx,
  borderColor: BORDER_COLOR,
  color: 'text.primary',
  bgcolor: '#fff',
  '&:hover': { borderColor: BORDER_COLOR, bgcolor: '#F9FAFB' }
} as const;

export default function RegisterBrandDrawer({ open, brand = null, onClose, onSubmitted }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: createBrand, isPending: creating } = useCreateA2pBrand();
  const { mutateAsync: updateBrand, isPending: updating } = useUpdateA2pBrand();
  const { data: businessProfile } = useGetBusinessProfile({ include: 'address,locations' });
  const submitting = creating || updating;
  const isResubmit = Boolean(brand?.id);
  const identityLocked = isResubmit;

  const { control, reset, watch, getValues, setValue, handleSubmit } = useForm<A2pBrandFormValues>({
    defaultValues: EMPTY_BRAND_FORM
  });

  const country = watch('country');
  const stateOptions = useMemo(() => [{ value: '', label: 'Select' }, ...stateOptionsForCountry(country)], [country]);

  useEffect(() => {
    if (!open) return;
    setConfirmOpen(false);
    setError(null);
    reset(brand ? brandToFormValues(brand) : { ...EMPTY_BRAND_FORM, ...brandDefaultsFromBusiness(businessProfile) });
    // Prefer brand data on resubmit; business profile is applied on first open / when it arrives later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brand, reset]);

  useEffect(() => {
    if (!open || brand || !businessProfile) return;

    const defaults = brandDefaultsFromBusiness(businessProfile);
    const current = getValues();
    (Object.keys(defaults) as (keyof typeof defaults)[]).forEach((key) => {
      const value = defaults[key];
      if (typeof value === 'string' && value && !String(current[key] || '').trim()) {
        setValue(key, value);
      }
    });
  }, [open, brand, businessProfile, getValues, setValue]);

  useEffect(() => {
    const currentState = getValues('state');
    if (currentState && !stateOptions.some((option) => option.value === currentState)) {
      setValue('state', '');
    }
  }, [stateOptions, getValues, setValue]);

  const openConfirm = handleSubmit(() => setConfirmOpen(true));

  const handleConfirm = async () => {
    setError(null);
    const payload = { a2p_brand: brandFormToPayload(getValues()) };
    try {
      const result = brand?.id ? await updateBrand({ id: brand.id, payload }) : await createBrand(payload);
      setConfirmOpen(false);
      onSubmitted(result);
    } catch (err) {
      setConfirmOpen(false);
      setError(apiErrorMessage(err, 'Unable to submit your brand. Please check the details and try again.'));
    }
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 900,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom={`1px solid ${BORDER_COLOR}`}
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          {isResubmit ? 'Resubmit Brand' : 'Register Brand'}
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={2} py={3}>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box
          display='grid'
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
          columnGap={2}
          rowGap={2}
          alignItems='start'
        >
          <Input
            name='display_name'
            control={control}
            label='DBA or Brand Name'
            placeholder='Enter Name Here'
            required
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ required: 'DBA or brand name is required' }}
          />
          <Select
            name='entity_type'
            control={control}
            label='Legal Form'
            options={ENTITY_TYPE_OPTIONS}
            fullWidth
            required
            disabled={identityLocked}
            displayEmpty
            sortOrder='none'
            MenuProps={selectMenuProps}
            helperText='What type of legal form is the organisation?'
            rules={{ required: 'Legal form is required' }}
          />

          <Input
            name='company_name'
            control={control}
            label='Legal Company Name'
            placeholder='Enter Name Here'
            required
            disabled={identityLocked}
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ required: 'Legal company name is required' }}
          />
          <Input
            name='ein'
            control={control}
            label='Tax Number/ ID/ EIN'
            placeholder='EIN 12-3456789'
            required
            disabled={identityLocked}
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ required: 'Tax number / EIN is required' }}
          />

          <TelInput
            name='phone'
            control={control}
            label='Phone Number'
            required
            defaultCountry='CA'
            onlyCountries={['CA', 'US']}
            InputLabelProps={shrinkLabel}
            rules={{ required: 'Phone number is required' }}
          />
          <Input
            name='email'
            control={control}
            label='Email'
            placeholder='abc@gmail.com'
            required
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' }
            }}
          />

          <Input
            name='business_contact_email'
            control={control}
            label='Business Contact Email'
            placeholder='abc@gmail.com'
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } }}
          />
          <Input
            name='street'
            control={control}
            label='Address'
            placeholder='8 Nelson Street...'
            required
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ required: 'Address is required' }}
          />

          <Input
            name='city'
            control={control}
            label='City'
            placeholder='Brampton'
            required
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ required: 'City is required' }}
          />
          <Input
            name='country'
            control={control}
            label='Country'
            placeholder='Canada'
            required
            disabled={identityLocked}
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ required: 'Country is required' }}
          />

          <Select
            name='state'
            control={control}
            label='State / Province / Region'
            options={stateOptions}
            fullWidth
            required
            searchable
            displayEmpty
            sortOrder='none'
            MenuProps={selectMenuProps}
            rules={{ required: 'State / province is required' }}
          />
          <Input
            name='postal_code'
            control={control}
            label='ZIP/Postal Code'
            placeholder='L6XC02'
            required
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ required: 'ZIP / postal code is required' }}
          />

          <Select
            name='vertical'
            control={control}
            label='Vertical Type'
            options={VERTICAL_OPTIONS}
            fullWidth
            required
            searchable
            displayEmpty
            sortOrder='none'
            MenuProps={selectMenuProps}
            rules={{ required: 'Vertical type is required' }}
          />
          <Input
            name='website'
            control={control}
            label='Website'
            placeholder='www.kickcall.ai'
            required
            fullWidth
            InputLabelProps={shrinkLabel}
            rules={{ required: 'Website is required' }}
          />
        </Box>
      </Box>

      <Box display='flex' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop={`1px solid ${BORDER_COLOR}`}>
        <Button variant='outlined' onClick={onClose} sx={{ ...outlinedPillSx, minWidth: 80 }}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' onClick={openConfirm} sx={{ ...pillSx, minWidth: 100 }}>
          Send for Review
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={submitting ? undefined : () => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: '4px', width: 452, maxWidth: 'calc(100% - 32px)', m: 2 } }}
      >
        <DialogContent sx={{ textAlign: 'center', px: 2, pt: 3, pb: 3 }}>
          <Typography fontSize={15} fontWeight={600} color='text.primary' mb={0.75}>
            Confirm Submission
          </Typography>
          <Typography fontSize={14} color='text.primary' mb={2.5}>
            {isResubmit
              ? 'Are you sure you want to resubmit your brand for review?'
              : 'Are you sure you want to submit brand registration?'}
          </Typography>
          <Stack direction='row' spacing={1.5} justifyContent='center'>
            <Button
              variant='outlined'
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              sx={{ ...outlinedPillSx, minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleConfirm}
              isPending={submitting}
              sx={{ ...pillSx, minWidth: 80 }}
            >
              Yes Sure
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Drawer>
  );
}
